import { Legislation } from "@/models/legislation/Legislation";
import {
  InboundChange,
  OutboundChange,
} from "@/models/legislation/metadata/ConsolidationMetadata";
import { RetsinformationMetadata } from "@/models/legislation/metadata/Retsinformation";
import { LegislationSearchResult } from "@/models/search/SearchResults";
import { DataSource, normalizeDataSource } from "@/config/datasources";
import {
  AppNavigationTarget,
  legislationTarget,
} from "@/lib/navigation/appNavigation";
import { resolveAnchorOpenNavigation } from "@/lib/consolidationMetadata";
import {
  ConsolidationProgressDisplay,
  getConsolidationProgressDisplay,
} from "@/lib/consolidationProgressDisplay";
import {
  appendTimelineAtParam,
  buildLegislationHtmlPath,
  buildTimelineUniverseNavigationPath,
  isChangelawDocumentType,
  isTimelineAmendmentLegislation,
  isTimelineUniverseChildLegislation,
} from "@/lib/timelineUniverseParams";
import { isRetsinformationMetadata } from "@/utils/legislationMetadataUtils";

export const parseInboundChange = (
  raw: Record<string, unknown>
): InboundChange | null => {
  const changelawAccession = (raw.changelawAccession ??
    raw.changelaw_accession) as string | undefined;
  const status = raw.status as InboundChange["status"] | undefined;
  const timelineRaw = raw.timelineIndex ?? raw.timeline_index;

  if (!changelawAccession || !status) return null;

  const timelineIndex =
    typeof timelineRaw === "number" ? timelineRaw : Number(timelineRaw);

  if (Number.isNaN(timelineIndex)) return null;

  const approvedAt = raw.approvedAt ?? raw.approved_at;
  const generatedAt = raw.generatedAt ?? raw.generated_at;

  return {
    changelawAccession,
    status,
    timelineIndex,
    ...(typeof approvedAt === "string" ? { approvedAt } : {}),
    ...(typeof generatedAt === "string" ? { generatedAt } : {}),
  };
};

const parseOutboundChange = (
  raw: Record<string, unknown>
): OutboundChange | null => {
  const anchorLawAccession = (raw.anchorLawAccession ??
    raw.anchor_law_accession) as string | undefined;
  const status = raw.status as OutboundChange["status"] | undefined;

  if (!anchorLawAccession || !status) return null;

  const approvedAt = raw.approvedAt ?? raw.approved_at;
  const generatedAt = raw.generatedAt ?? raw.generated_at;

  return {
    anchorLawAccession,
    status,
    ...(typeof approvedAt === "string" ? { approvedAt } : {}),
    ...(typeof generatedAt === "string" ? { generatedAt } : {}),
  };
};

const parseConsolidationArrays = (
  parsed: Record<string, unknown>
): Pick<RetsinformationMetadata, "inboundChanges" | "outboundChanges"> => {
  const inboundRaw = parsed.inboundChanges ?? parsed.inbound_changes;
  const outboundRaw = parsed.outboundChanges ?? parsed.outbound_changes;

  const inboundChanges = Array.isArray(inboundRaw)
    ? inboundRaw
        .map((entry) =>
          entry && typeof entry === "object"
            ? parseInboundChange(entry as Record<string, unknown>)
            : null
        )
        .filter((entry): entry is InboundChange => entry !== null)
    : undefined;

  const outboundChanges = Array.isArray(outboundRaw)
    ? outboundRaw
        .map((entry) =>
          entry && typeof entry === "object"
            ? parseOutboundChange(entry as Record<string, unknown>)
            : null
        )
        .filter((entry): entry is OutboundChange => entry !== null)
    : undefined;

  return { inboundChanges, outboundChanges };
};

const parseJsonRecord = (
  value: unknown
): Record<string, unknown> | null => {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value) as unknown;
      return parsed && typeof parsed === "object"
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }
  if (typeof value === "object") {
    return value as Record<string, unknown>;
  }
  return null;
};

const resolveConsolidationMetadataRaw = (
  legislation: Legislation
): unknown =>
  legislation.consolidationMetadata ??
  (legislation as Legislation & { consolidation_metadata?: unknown })
    .consolidation_metadata;

const parseConsolidationMetadataColumn = (
  consolidationMetadata: unknown
): Pick<RetsinformationMetadata, "inboundChanges" | "outboundChanges"> => {
  const parsed = parseJsonRecord(consolidationMetadata);
  if (!parsed) return {};
  return parseConsolidationArrays(parsed);
};

const mergeConsolidationMetadataFields = (
  base: RetsinformationMetadata | null,
  fromColumn: Pick<RetsinformationMetadata, "inboundChanges" | "outboundChanges">
): RetsinformationMetadata | null => {
  const hasColumnInbound = (fromColumn.inboundChanges?.length ?? 0) > 0;
  const hasColumnOutbound = (fromColumn.outboundChanges?.length ?? 0) > 0;

  if (!base && !hasColumnInbound && !hasColumnOutbound) {
    return null;
  }

  const merged: RetsinformationMetadata = {
    ...(base ?? {}),
  };

  if (hasColumnInbound) {
    merged.inboundChanges = fromColumn.inboundChanges;
  }
  if (hasColumnOutbound) {
    merged.outboundChanges = fromColumn.outboundChanges;
  }

  return merged;
};

const parseMetadataObject = (
  metadataObject: unknown
): RetsinformationMetadata | null => {
  const parsed = parseJsonRecord(metadataObject);
  if (!parsed) return null;

  const hasRetsinformationShape =
    "accessionNumber" in parsed ||
    "accession_number" in parsed ||
    "documentTypeId" in parsed ||
    "document_type_id" in parsed;

  if (!hasRetsinformationShape) return null;

  const consolidation = parseConsolidationArrays(parsed);

  return {
    ...(parsed as RetsinformationMetadata),
    ...consolidation,
  };
};

const parseConsolidationOnlyMetadata = (
  metadata: unknown
): RetsinformationMetadata | null => {
  const parsed = parseJsonRecord(metadata);
  if (!parsed) return null;

  const consolidation = parseConsolidationArrays(parsed);
  const hasInbound = (consolidation.inboundChanges?.length ?? 0) > 0;
  const hasOutbound = (consolidation.outboundChanges?.length ?? 0) > 0;
  if (!hasInbound && !hasOutbound) return null;

  return {
    ...(parsed as RetsinformationMetadata),
    ...consolidation,
  };
};

/** Resolves Retsinformation metadata from a legislation row (metadata, metadataObject, consolidationMetadata). */
export const getRetsinformationMetadataFromLegislation = (
  legislation: Legislation
): RetsinformationMetadata | null => {
  if (
    normalizeDataSource(legislation.datasource) !== DataSource.Retsinformation
  ) {
    return null;
  }

  const fromColumn = parseConsolidationMetadataColumn(
    resolveConsolidationMetadataRaw(legislation)
  );

  if (isRetsinformationMetadata(legislation.metadata)) {
    const fromTyped = mergeConsolidationMetadataFields(
      legislation.metadata,
      parseConsolidationArrays(legislation.metadata as Record<string, unknown>)
    );
    // Live list/search enrichment writes inbound onto typed metadata. Prefer that
    // over the stale silver consolidationMetadata column (which previously always
    // won and forced APPROVED back to NEED_REVIEW on the overview badge).
    const typedHasInbound = Array.isArray(fromTyped?.inboundChanges);
    const columnForMerge = typedHasInbound
      ? { outboundChanges: fromColumn.outboundChanges }
      : fromColumn;
    return mergeConsolidationMetadataFields(fromTyped, columnForMerge);
  }

  const fromObject =
    parseMetadataObject(legislation.metadataObject) ??
    parseMetadataObject(legislation.metadata) ??
    parseConsolidationOnlyMetadata(legislation.metadata);
  return mergeConsolidationMetadataFields(fromObject, fromColumn);
};

export const resolveLegislationOpenNavigationTarget = (
  legislation: Legislation,
  consolidationFeatureEnabled: boolean
): AppNavigationTarget => {
  const useHtml =
    legislation.htmlAvailable || legislation.htmlProcessingIncomplete;

  if (!useHtml) {
    return {
      kind: "legislationPdf",
      documentId: legislation.externalId,
      source: legislation.datasource,
      context: "app",
    };
  }

  if (consolidationFeatureEnabled) {
    const meta = getRetsinformationMetadataFromLegislation(legislation);
    if (meta?.inboundChanges?.length) {
      const path = resolveAnchorOpenNavigation(meta, legislation.externalId);
      if (path) {
        return { kind: "path", path };
      }
    }
  }

  return {
    kind: "legislation",
    documentId: legislation.externalId,
    source: legislation.datasource,
    context: "app",
  };
};

const buildLegislationFromSearchResult = (
  result: LegislationSearchResult
): Legislation => ({
  internalId: result.id,
  externalId: result.id,
  title: result.title,
  datasource: result.source ?? DataSource.Retsinformation,
  metadata: result.metadata,
  htmlAvailable: result.htmlAvailable,
  htmlProcessingIncomplete: result.htmlProcessingIncomplete,
});

export const resolveSearchLegislationOpenNavigationTarget = (
  result: LegislationSearchResult,
  consolidationFeatureEnabled: boolean
): AppNavigationTarget =>
  resolveLegislationOpenNavigationTarget(
    buildLegislationFromSearchResult(result),
    consolidationFeatureEnabled
  );

/**
 * Consolidation-progress display for a legislation search result, using the
 * same logic as the legislation overview table. Returns null when the result
 * is not an eligible Retsinformation Lov/LBK/BEK with timeline amendments.
 */
export const getSearchResultConsolidationDisplay = (
  result: LegislationSearchResult
): ConsolidationProgressDisplay | null => {
  const meta = getRetsinformationMetadataFromLegislation(
    buildLegislationFromSearchResult(result)
  );

  return getConsolidationProgressDisplay({
    inboundChanges: meta?.inboundChanges,
    timelineAmendmentCountAfter: result.timelineAmendmentCountAfter,
    documentTypeId: meta?.documentTypeId,
  });
};

export type ResolveTimelineNavigationOptions = {
  targetAccessionNumber: string;
  datasource: string;
  timelineRootAccession?: string;
  flatTimelineIndex?: number;
};

/**
 * Timeline navigation always uses real Retsinformation document routes.
 * Consolidation renders are chosen explicitly via the node chooser or outbound panel.
 */
export const resolveTimelineNavigationTarget = ({
  targetAccessionNumber,
  datasource,
  timelineRootAccession,
  flatTimelineIndex,
}: ResolveTimelineNavigationOptions): AppNavigationTarget => {
  if (
    timelineRootAccession &&
    timelineRootAccession !== targetAccessionNumber
  ) {
    return {
      kind: "path",
      path: buildTimelineUniverseNavigationPath(
        targetAccessionNumber,
        datasource,
        timelineRootAccession,
        flatTimelineIndex
      ),
    };
  }

  if (flatTimelineIndex != null) {
    return {
      kind: "path",
      path: appendTimelineAtParam(
        buildLegislationHtmlPath(targetAccessionNumber, datasource),
        flatTimelineIndex
      ),
    };
  }

  return legislationTarget(targetAccessionNumber, datasource, "app");
};

export const resolveAnchorLawAccessionForTimeline = (
  documentId: string | undefined,
  meta: RetsinformationMetadata | null
): string | undefined => {
  if (!documentId?.trim()) return undefined;
  if (meta?.inboundChanges?.length) return documentId;
  return undefined;
};

export const legislationUsesParentTimeline = (
  meta: RetsinformationMetadata | null | undefined,
  timelineRoot: string | null,
  documentId?: string | null
): boolean => {
  if (!timelineRoot) return false;
  if (meta) {
    return isTimelineUniverseChildLegislation(meta.documentTypeId, {
      timelineRoot,
      documentId,
    });
  }
  // Metadata still loading: changelaw pages in a timeline universe set
  // timeline_root to the anchor and use a different route document id.
  if (documentId && documentId !== timelineRoot) return true;
  return false;
};

export {
  isChangelawDocumentType,
  isTimelineAmendmentLegislation,
  isTimelineUniverseChildLegislation,
};
